import {
  hasRealRedactionFill,
  isTextOnlyOverlayGroup,
  resolveTextOverlayRect,
} from '@helpers/textOverlay';

import {
  BoundingPolyRect,
  UDRsBasePoly,
  UDRsPolyAsset,
  UDRsPolyAssetStatus,
  UDRsPolyAssetGroup,
  UDRsPolyAssetGroupSeriesItem,
  ShapeType,
  RedactionConfig,
  BlurLevel,
  TextOverlay,
} from '@common-modules/mediaDetails/models';
import { Adapter } from '../interfaces';
import { IndividualRedactionCode } from '@redact/state/modules/redactionCodes/models';
import { numberToBlurLevel } from '@common/shared/util';

/**
 * Added on 2019.3.15.
 * Required by switch to new V2F OT engine.
 * [Veritone - Object Tracker - V2F](https://github.com/veritone/task-veritone-object-tracker)
 */
export interface UDRsAssetV2 {
  readonly version: string;
  readonly boundingPolys: {
    readonly [groupId: string]:
      | null
      | (UDRsPolyAssetGroupV2 & UDRsPolyAssetGroupLegacy);
  };
  // Text overlays are serialized under their own key so the on-disk format
  // keeps them separate from redaction bounding polys. In the Redux store they
  // still live inside `boundingPolys` (see the adapters below).
  readonly textOverlays?: {
    readonly [groupId: string]:
      | null
      | (UDRsPolyAssetGroupV2 & UDRsPolyAssetGroupLegacy);
  };
}

export interface UDRsPolyAssetGroupV2 {
  readonly userLabel: string;
  // readonly groupId: string; // don't really need this
  readonly clusterId?: string;
  readonly status?: UDRsPolyAssetStatus;
  readonly jobId?: string;
  // Optional: not written for text overlays, which have no OT engine seed, and
  // absent on anything saved before the field existed (VE-30942).
  readonly basePoly?: UDRsBasePoly;
  readonly series: ReadonlyArray<
    UDRsAssetV2SeriesItem | UDRsPolyAssetGroupSeriesItem
  >;
  readonly shapeType?: ShapeType;
  readonly redactionCode?: IndividualRedactionCode;
  readonly textOverlay?: TextOverlay;
  readonly redactionConfig?: RedactionConfig;
}

export interface UDRsPolyAssetGroupLegacy {
  readonly blurLevel?: BlurLevel;
  readonly blackFill?: boolean;
}

export interface UDRsAssetV2SeriesItem {
  readonly id: string;
  readonly startTimeMs: number;
  readonly stopTimeMs: number;
  readonly object: {
    readonly boundingPoly: BoundingPolyRect;
  };
}
export const REDACTIONTYPE_BLACKFILL = 'black_fill' as const;
export const REDACTIONTYPE_BLUR = 'blur' as const;

// Handle legacy values; blurLevel and blackFill
export const getLegacyRedactionConfig = (
  p: UDRsPolyAssetGroupLegacy
): RedactionConfig | undefined => {
  if (p.blurLevel || p.blackFill !== undefined) {
    return {
      fillType: p.blackFill ? REDACTIONTYPE_BLACKFILL : REDACTIONTYPE_BLUR,
      blurLevel: numberToBlurLevel(p.blurLevel) || 10,
    };
  }
  return undefined;
};

export const udrsAssetV2Adapter = (): Adapter<UDRsAssetV2, UDRsPolyAsset> => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
  const isMatch = (i: any): i is UDRsAssetV2 => i?.version?.startsWith('2.0');

  const mapAssetGroup = (
    groupId: string,
    p: (UDRsPolyAssetGroupV2 & UDRsPolyAssetGroupLegacy) | null | undefined
  ): UDRsPolyAssetGroup | undefined => {
    if (!p) {
      return undefined;
    }

    const series = p.series.map(assetMapper);

    // Lazy upgrade (VE-30942): a text overlay's authored position now lives on
    // the overlay itself. Assets written before that carry it only as the
    // series rect, so hoist it here and they upgrade on open — no migration,
    // and the next save writes the new shape. A pinned overlay needs no
    // customRect at all: its box is derived from the pins.
    const textOverlay =
      p.textOverlay &&
      !p.textOverlay.customRect &&
      !p.textOverlay.screenPositionHorizontal &&
      !p.textOverlay.screenPositionVertical &&
      series[0]
        ? { ...p.textOverlay, customRect: series[0].object.boundingPoly }
        : p.textOverlay;

    return {
      userLabel: p.userLabel,
      groupId,
      clusterId: p.clusterId,
      status:
        p.status ||
        (p.series.length > 1
          ? UDRsPolyAssetStatus.GROUP
          : UDRsPolyAssetStatus.SINGLE),
      jobId: p.jobId,
      basePoly: p.basePoly,
      series,
      shapeType: p.shapeType,
      redactionCode: p.redactionCode,
      textOverlay,
      redactionConfig: p.redactionConfig
        ? p.redactionConfig
        : getLegacyRedactionConfig(p),
    };
  };

  const adapt = (i: UDRsAssetV2): UDRsPolyAsset => {
    // Text overlays are stored separately on disk but merged back into a single
    // `boundingPolys` map for the store (group ids are disjoint across the two).
    const sources = { ...i.boundingPolys, ...(i.textOverlays ?? {}) };
    const boundingPolys: { [groupId: string]: UDRsPolyAssetGroup | null } = {};
    for (const groupId of Object.keys(sources)) {
      const mappedPoly = mapAssetGroup(groupId, sources[groupId]);
      if (mappedPoly) {
        boundingPolys[groupId] = mappedPoly;
      }
    }
    return { boundingPolys };
  };

  return { isMatch, adapt };
};

export const udrsStoreV2Adapter = (): Adapter<UDRsPolyAsset, UDRsAssetV2> => {
  // eslint-disable-next-line lodash/prefer-constant
  const isMatch = (_: unknown): _ is UDRsPolyAsset => true;

  const mapStoreGroup = (p: UDRsPolyAssetGroup) => {
    // Fail-safe (see hasRealRedactionFill): a group that somehow carried both
    // text and a real fill keeps every redaction field, because pruning them
    // here would turn it into a pure text overlay on the next load and stop
    // redacting the region for good.
    const isTextOverlay = isTextOnlyOverlayGroup(p) && !hasRealRedactionFill(p);

    // The series rect is a DERIVED CACHE for the burn-in engine, which needs a
    // box per time range. The authored position lives on the overlay, so fan
    // the one resolved box out across every segment rather than trusting
    // whatever each segment happens to be holding (VE-30942).
    const groupRect = isTextOverlay ? resolveTextOverlayRect(p) : undefined;
    const series = p.series
      .map(storeMapper)
      .map((s) =>
        groupRect
          ? { ...s, object: { ...s.object, boundingPoly: groupRect } }
          : s
      );

    return {
      // label: p.groupId, // don't think any reason to save this - is never loaded
      userLabel: p.userLabel,
      parentId: p.groupId, // CTODO keep save parentId for now so legacy versions of app can still load results - eventually can delete because we can pull the groupId from the key of this object
      ...(p.clusterId !== p.groupId && { clusterId: p.clusterId }), // save clusterId (when not same as groupId)
      series,
      redactionCode: p.redactionCode,
      textOverlay: p.textOverlay,
      // Fields that only mean something for a redaction. A text overlay never
      // goes to the OT engine, so basePoly (its seed) and status (SINGLE/GROUP
      // is series.length restated; PENDING is unreachable without a job) are
      // dead weight that only drifted. shapeType is write-only — drawTextOverlay
      // ignores it, and rectangle is the only value it can take. redactionConfig
      // was the fillType type-tag hack, no longer read. See VE-30942.
      ...(isTextOverlay
        ? {}
        : {
            status: p.status,
            jobId: p.jobId,
            basePoly: p.basePoly,
            shapeType: p.shapeType,
            redactionConfig: p.redactionConfig,
          }),
    };
  };

  const adapt = (i: UDRsPolyAsset): UDRsAssetV2 => {
    // Partition the store's single `boundingPolys` map into redactions and
    // text-only overlays so each is serialized under its own key.
    const boundingPolys: {
      [groupId: string]: ReturnType<typeof mapStoreGroup> | null;
    } = {};
    const textOverlays: {
      [groupId: string]: ReturnType<typeof mapStoreGroup> | null;
    } = {};

    for (const [groupId, p] of Object.entries(i.boundingPolys)) {
      if (!p) {
        boundingPolys[groupId] = null;
        continue;
      }
      // Same fail-safe as mapStoreGroup: a group with a real redaction fill
      // stays under `boundingPolys` even if it also carries text, so it is
      // still loaded and exported as a redaction.
      const target =
        isTextOnlyOverlayGroup(p) && !hasRealRedactionFill(p)
          ? textOverlays
          : boundingPolys;
      target[groupId] = mapStoreGroup(p);
    }

    return { version: '2.0', boundingPolys, textOverlays };
  };

  return { isMatch, adapt };
};

const assetMapper = (s: UDRsAssetV2SeriesItem) => ({
  id: s.id,
  startTimeMs: s.startTimeMs,
  stopTimeMs: s.stopTimeMs,
  object: {
    id: s.id,
    overlayObjectType:
      (<UDRsPolyAssetGroupSeriesItem>s).object.overlayObjectType ||
      ('udr' as const),
    boundingPoly: s.object.boundingPoly,
  },
});

const storeMapper = (s: UDRsAssetV2SeriesItem) => ({
  id: s.id,
  startTimeMs: s.startTimeMs,
  stopTimeMs: s.stopTimeMs,
  object: {
    boundingPoly: s.object.boundingPoly,
    overlayObjectType:
      (<UDRsPolyAssetGroupSeriesItem>s).object.overlayObjectType ||
      ('udr' as const),
  },
});
